import {
  ReportSection,
  ReportSectionItemContent,
  ReportSectionItemTable,
} from '@/components';
import { useIndicators } from '@/features/project/_hooks/activity';
import type { ProjectModel } from '@/features/project/projects/_models/project';
import { useId } from '@/hooks';
import { FALLBACK_TEXT } from '@/utils';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

type Props = {
  project: z.infer<typeof ProjectModel>;
};
export const RealisasiProgram: FC<Props> = ({ project }) => {
  const fiscalYear = project.fiscalYear;
  const { t } = useTranslation();
  const { id: projectId } = useId();
  const { data: indicatorsData } = useIndicators({
    filters: {
      keyId: projectId,
      ordering: '-type,-term,sort_order',
    },
  });

  const outputs =
    indicatorsData?.filter((indicator) => {
      return indicator.type === 'output';
    }) ?? [];
  const outcome =
    indicatorsData?.filter((indicator) => {
      return indicator.type === 'outcome';
    }) ?? [];

  return (
    <ReportSection
      title={t('title-realisasi-program')}
      items={[
        {
          indexText: '21.1',
          subtitle: t('indicator-type-output'),
          item: (
            <ReportSectionItemTable
              header={[
                {
                  type: 'text',
                  value: t('indicator-description'),
                  textStyle: { textAlign: 'center' },
                  ratio: 40,
                },
                {
                  type: 'text',
                  value: t('unit'),
                  textStyle: { textAlign: 'center' },
                  ratio: 20,
                },
                {
                  type: 'text',
                  value: t('tahun-year', { year: fiscalYear }),
                  textStyle: { textAlign: 'center' },
                  ratio: 20,
                },
                {
                  type: 'text',
                  value: t('tahun-year', { year: fiscalYear - 1 }),
                  textStyle: { textAlign: 'center' },
                  ratio: 20,
                },
              ]}
              data={
                outputs.length
                  ? outputs.map((output) => {
                      return {
                        row: [
                          { type: 'text', value: output.description },
                          { type: 'text', value: output.unit },
                          { type: 'text', value: output.reason },
                          { type: 'text', value: output.noTypeReason },
                        ],
                      };
                    })
                  : [{ row: [{ type: 'text', value: FALLBACK_TEXT }] }]
              }
            />
          ),
        },
        {
          indexText: '21.2',
          subtitle: t('indicator-type-outcome'),
          item: (
            <ReportSectionItemTable
              header={[
                {
                  type: 'text',
                  value: t('indicator-description'),
                  textStyle: { textAlign: 'center' },
                  ratio: 40,
                },
                {
                  type: 'text',
                  value: t('unit'),
                  textStyle: { textAlign: 'center' },
                  ratio: 20,
                },
                {
                  type: 'text',
                  value: t('tahun-year', { year: fiscalYear }),
                  textStyle: { textAlign: 'center' },
                  ratio: 20,
                },
                {
                  type: 'text',
                  value: t('tahun-year', { year: fiscalYear - 1 }),
                  textStyle: { textAlign: 'center' },
                  ratio: 20,
                },
              ]}
              data={
                outcome.length
                  ? outcome.map((outcome) => {
                      return {
                        row: [
                          { type: 'text', value: outcome.description },
                          { type: 'text', value: outcome.unit },
                          { type: 'text', value: outcome.reason },
                          { type: 'text', value: outcome.noTypeReason },
                        ],
                      };
                    })
                  : [{ row: [{ type: 'text', value: FALLBACK_TEXT }] }]
              }
            />
          ),
        },
        {
          indexText: '21.',
          subtitle: t('analisis-kesesuaian-penggunaan-anggaran'),
          item: (
            <ReportSectionItemContent
              value={project.cabinetDecisionWithDefinedIndicators?.name}
            />
          ),
        },
        {
          indexText: '22.',
          subtitle: t('analisis-capaian-tujuan-program'),
          item: (
            <ReportSectionItemContent
              value={project.cabinetDecisionWithDefinedIndicators?.relevantPart}
            />
          ),
        },
      ]}
    />
  );
};
